import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { formatFecha, formatFechaWithHour } from "../../../utils/dateFunctions";
import EstadoBadge from "./EstadoBadge";
import { useState } from "react";
import ConfirmDialog from "../../utils/ConfirmDialog";

const SuscriptTable = () => {
  const { suscriptions, removeUserFromSuscription, myOrgs } = useSingleUserControl();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setIdToDelete(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (idToDelete) {
      setConfirmOpen(false);
      setLoadingId(idToDelete);
      await removeUserFromSuscription(idToDelete);
      setLoadingId(null);
      setIdToDelete(null);
    }
  };

  const estadoMap: Record<string, "success" | "warning" | "wrong" | "unknown"> =
    {
      Activo: "success",
      Inactivo: "warning",
      Eliminado: "wrong",
    };

  return (
    <>
      <TableContainer
        sx={{
          fontFamily: "Montserrat, sans-serif",
          boxShadow: "none",
          color: "#005559",
          px: 2,
        }}
      >
        <Table
          sx={{
            borderCollapse: "collapse",
            "& .MuiTableCell-root": {
              fontFamily: "Montserrat, sans-serif",
              padding: "6px 12px",
              borderBottom: "none",
              color: "#005559",
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                borderBottom: "1px solid #336B8780", // más gruesa para el header
              }}
            >
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Organización</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fecha de Inicio</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fecha de Expiración</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Créditos Usados</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suscriptions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{
                    fontStyle: "italic",
                    color: "#7A7A7A",
                    py: 2,
                  }}
                >
                  No hay suscripciones
                </TableCell>
              </TableRow>
            ) : (
              suscriptions.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    borderBottom:
                      index === suscriptions.length - 1
                        ? "none"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <TableCell>{row.plan}</TableCell>
                  <TableCell>
                    {row.organizacion ??
                      myOrgs.find((o) => o.id_organizacion === row.id_organizacion)
                        ?.organizacion ??
                      "-"}
                  </TableCell>
                  <TableCell>{formatFechaWithHour(row.fecha_asignacion)}</TableCell>
                  <TableCell>{formatFecha(row.fecha_fin)}</TableCell>
                  <TableCell>{row.creditos_gastados}</TableCell>
                  <TableCell>
                    <EstadoBadge estado={estadoMap[row.estado]}>
                      {row.estado}
                    </EstadoBadge>
                  </TableCell>
                  <TableCell>
                    {loadingId === row.id_suscripcion ? (
                      <CircularProgress size={20} color="error" />
                    ) : (
                      <Tooltip title="Eliminar Suscripción">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(row.id_suscripcion)}
                          sx={{
                            color: "#d32f2f",
                            "&:hover": { backgroundColor: "#d32f2f15" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Suscripción"
        description="¿Estás seguro de que deseas eliminar esta suscripción para el usuario? Esta acción no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default SuscriptTable;
